import {
  LinkStyleButton,
  SelectionInputLayout,
  Toggle,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  type EmailRegistrationPolicy,
  type RegistrationFormConfigurationInput,
  useUpdateEmailRegistrationPolicyMutation,
} from 'src/generated/graphql-manager';

import { RegistrationFieldsModal } from '../registration-fields-modal';

import styles from './email-registration-policy.module.css';

type Props = {
  emailRegistrationPolicy?: EmailRegistrationPolicy;
  allowDisablingOnly: boolean;
  formDisabled?: boolean;
  refetch: () => void;
};

export const defaultRegistrationFormFields = {
  firstAndLastName: false,
  jobTitle: false,
  companyName: false,
  departmentName: false,
  phoneNumber: false,
  country: false,
  bankCode: false,
};

export function EmailRegistrationPolicy({ emailRegistrationPolicy, allowDisablingOnly, formDisabled, refetch }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();

  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);

  const registrationEnabled = emailRegistrationPolicy?.enabled;

  const registrationForm = emailRegistrationPolicy?.registrationFormConfiguration || {};

  const [updateEmailRegistrationPolicyMutation, { loading: updateEmailRegistrationPolicyLoading }] =
    useUpdateEmailRegistrationPolicyMutation();

  const updateEmailRegistrationPolicy = ({
    enabled = Boolean(emailRegistrationPolicy?.enabled),
    registrationForm,
  }: {
    enabled?: boolean;
    registrationForm: RegistrationFormConfigurationInput | null;
  }) => {
    if (updateEmailRegistrationPolicyLoading) return;

    updateEmailRegistrationPolicyMutation({
      variables: {
        input: {
          webcastId,
          enabled,
          registrationFormConfigurationInput: registrationForm,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateEmailRegistrationPolicy.__typename === 'UpdateEmailRegistrationPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const handleRegistrationToggleChange = () => {
    if (registrationEnabled) {
      updateEmailRegistrationPolicy({ enabled: false, registrationForm: defaultRegistrationFormFields });
    } else {
      setFieldsModalOpen(true);
    }
  };

  const handleConfirm = (registrationForm: RegistrationFormConfigurationInput) => {
    setFieldsModalOpen(false);
    updateEmailRegistrationPolicy({ enabled: true, registrationForm });
  };

  return (
    <>
      <SelectionInputLayout
        variant="ghost"
        checked={registrationEnabled}
        disabled={(allowDisablingOnly && !registrationEnabled) || formDisabled}
        inputElement={
          <Toggle
            data-testid="email-registration-policy-toggle"
            disabled={(allowDisablingOnly && !registrationEnabled) || formDisabled}
            checked={registrationEnabled}
            onChange={handleRegistrationToggleChange}
          >
            {t('manager.webcastSetup.eventAccess.registration.formToggle')}
          </Toggle>
        }
      >
        {registrationEnabled && (
          <div className={styles.form}>
            <div className={styles.formTitle}>
              {t('manager.webcastSetup.eventAccess.registration.selectedFieldsTitle')}
            </div>

            <div className={styles.formFields}>
              {registrationForm.email && (
                <div data-testid="field-email">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.email')}
                </div>
              )}

              {registrationForm.firstAndLastName && (
                <div data-testid="field-firstAndLastName">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.firstAndLastName')}
                </div>
              )}

              {registrationForm.companyName && (
                <div data-testid="field-companyName">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.companyName')}
                </div>
              )}

              {registrationForm.departmentName && (
                <div data-testid="field-departmentName">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.departmentName')}
                </div>
              )}

              {registrationForm.jobTitle && (
                <div data-testid="field-jobTitle">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.jobTitle')}
                </div>
              )}

              {registrationForm.country && (
                <div data-testid="field-country">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.country')}
                </div>
              )}

              {registrationForm.phoneNumber && (
                <div data-testid="field-phoneNumber">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.phoneNumber')}
                </div>
              )}

              {registrationForm.bankCode && (
                <div data-testid="field-bankCode">
                  {t('manager.webcastSetup.eventAccess.registration.fieldsOptions.bankCode')}
                </div>
              )}
            </div>

            <LinkStyleButton
              data-testid="edit-form-button"
              disabled={allowDisablingOnly}
              className={styles.editFormButton}
              onClick={() => setFieldsModalOpen(true)}
            >
              {t('manager.webcastSetup.eventAccess.registration.editFormButton')}
            </LinkStyleButton>
          </div>
        )}
      </SelectionInputLayout>

      {fieldsModalOpen && (
        <RegistrationFieldsModal
          onConfirm={handleConfirm}
          onClose={() => setFieldsModalOpen(false)}
          registrationForm={registrationForm}
        />
      )}
    </>
  );
}
